import { useRef } from 'react';
import { ESTIMATE_TAG, EMPTY_TEXT } from '../labels';
import { formatCompact, formatKc } from '../format';
import {
  BAR_COLORS,
  GRID_LINES,
  compareColor,
  nextChartId,
  niceMax,
  scale,
  ticks,
  truncate,
} from './chart-utils';
import styles from '../styles.module.scss';

// charts/month-bars.tsx — cột đứng, mỗi tháng một cột (SPEC Studio mục 8.7.7, 8.8).
export interface MonthBarsDatum {
  month: string; // YYYY-MM
  label: string; // 'MM/YYYY'
  subLabel?: string; // chỉ khi sameDay=1: cut.label của cặp mà cột này thuộc về (bảng ở mục 8.7.7)
  value: number | null; // sameDay=1: số ĐÃ CẮT ; sameDay=0: fullTotals. null = không có dữ liệu → cột gạch chéo + chữ "—"
  role: 'BASE' | 'COMPARE';
  estimated?: boolean; // viền nét đứt ; chỉ có nghĩa với số đã cắt (sameDay=1)
}
export interface MonthBarsProps {
  data: MonthBarsDatum[]; // 2–4 phần tử, tháng gốc đứng đầu
  caption?: string; // tiêu đề phụ của biểu đồ (bảng ở mục 8.7.7)
  height?: number; // mặc định 220
  formatValue?: (n: number) => string; // mặc định formatCompact (1,2 tr · 850 N)
  ariaLabel: string;
}

const WIDTH = 560;
const PAD_TOP = 22;
const PAD_BOTTOM = 40;
const PAD_LEFT = 58;
const PAD_RIGHT = 12;
const EMPTY_BAR_RATIO = 0.18;

/**
 * Cột đứng theo tháng, SVG tự vẽ: trục từ 0, 4 vạch lưới làm tròn bằng niceMax, số trên đầu cột và nhãn
 * tháng dưới cột (không phụ thuộc rê chuột). Tháng gốc màu accent, tháng so sánh lilac / peach / sun; cột
 * ước tính viền nét đứt + thẻ "ước tính"; tháng không có dữ liệu là cột gạch chéo + "—". Chú giải bên dưới
 * lặp lại đủ số (đầy đủ chữ số) và `subLabel` của từng cột.
 */
const MonthBars = ({
  data,
  caption,
  height = 220,
  formatValue = formatCompact,
  ariaLabel,
}: MonthBarsProps) => {
  const idRef = useRef(nextChartId('month-bars'));
  const patternId = `${idRef.current}-empty`;

  const values = data.map((d) => d.value).filter((v): v is number => typeof v === 'number');
  const hasValue = values.some((v) => v > 0);
  const top = niceMax(values.length ? Math.max(...values) : 0);
  const plotH = height - PAD_TOP - PAD_BOTTOM;
  const plotW = WIDTH - PAD_LEFT - PAD_RIGHT;
  const baseY = PAD_TOP + plotH;
  const slot = data.length ? plotW / data.length : plotW;
  const barW = Math.min(64, slot * 0.56);

  let compareIndex = 0;
  const bars = data.map((d) => {
    const color = d.role === 'BASE' ? BAR_COLORS.base : compareColor(compareIndex++);
    return { ...d, color };
  });

  if (!data.length) {
    return (
      <figure className={styles.chart}>
        {caption && <figcaption className={styles.chartCaption}>{caption}</figcaption>}
        <div className={styles.chartEmpty}>{EMPTY_TEXT.chart}</div>
      </figure>
    );
  }

  return (
    <figure className={styles.chart}>
      {caption && <figcaption className={styles.chartCaption}>{caption}</figcaption>}
      <svg
        className={styles.chartSvg}
        viewBox={`0 0 ${WIDTH} ${height}`}
        width="100%"
        role="img"
        aria-label={ariaLabel}
        preserveAspectRatio="xMidYMid meet"
      >
        <title>{ariaLabel}</title>
        <defs>
          <pattern
            id={patternId}
            width="6"
            height="6"
            patternUnits="userSpaceOnUse"
            patternTransform="rotate(45)"
          >
            <rect width="6" height="6" fill={BAR_COLORS.empty} />
            <line x1="0" y1="0" x2="0" y2="6" stroke={BAR_COLORS.emptyLine} strokeWidth="2" />
          </pattern>
        </defs>

        {/* Lưới ngang + nhãn trục (đường 0 là trục hoành). */}
        {ticks(top, GRID_LINES).map((t) => {
          const y = baseY - scale(t, top, plotH);
          return (
            <g key={t}>
              <line
                x1={PAD_LEFT}
                x2={WIDTH - PAD_RIGHT}
                y1={y}
                y2={y}
                stroke={BAR_COLORS.grid}
                strokeWidth={t === 0 ? 1.5 : 1}
              />
              {hasValue && (
                <text
                  x={PAD_LEFT - 8}
                  y={y + 4}
                  textAnchor="end"
                  fontSize="11"
                  fill={BAR_COLORS.axis}
                >
                  {formatValue(t)}
                </text>
              )}
            </g>
          );
        })}

        {bars.map((d, i) => {
          const cx = PAD_LEFT + slot * i + slot / 2;
          const x = cx - barW / 2;
          const empty = d.value === null;
          const minH = d.value ? 2 : 0; // số dương rất nhỏ vẫn thấy được cột
          const h = empty
            ? plotH * EMPTY_BAR_RATIO
            : Math.max(minH, scale(d.value ?? 0, top, plotH));
          const y = baseY - h;
          const dashed = !empty && !!d.estimated;
          return (
            <g key={`${d.month}-${i}`}>
              <title>
                {`${d.label}: ${empty ? 'không có dữ liệu' : formatKc(d.value)}${
                  dashed ? ` (${ESTIMATE_TAG})` : ''
                }${d.subLabel ? ` · ${d.subLabel}` : ''}`}
              </title>
              <rect
                x={x}
                y={y}
                width={barW}
                height={h}
                rx={4}
                fill={empty ? `url(#${patternId})` : d.color}
                fillOpacity={dashed ? 0.55 : 1}
                stroke={empty ? BAR_COLORS.emptyLine : dashed ? BAR_COLORS.estimateInk : 'none'}
                strokeWidth={empty || dashed ? 1.5 : 0}
                strokeDasharray={dashed ? '5 3' : undefined}
              />
              <text
                x={cx}
                y={y - 6}
                textAnchor="middle"
                fontSize="12"
                fontWeight={600}
                fill={BAR_COLORS.value}
              >
                {empty ? '—' : formatValue(d.value as number)}
              </text>
              <text x={cx} y={baseY + 16} textAnchor="middle" fontSize="12" fill={BAR_COLORS.axis}>
                {truncate(d.label, 12)}
              </text>
              {dashed && (
                <text
                  x={cx}
                  y={baseY + 31}
                  textAnchor="middle"
                  fontSize="11"
                  fontWeight={600}
                  fill={BAR_COLORS.estimateInk}
                >
                  {ESTIMATE_TAG}
                </text>
              )}
            </g>
          );
        })}
      </svg>

      {/* Chú giải = bảng số của biểu đồ (mục 8.8: biểu đồ không phải nguồn duy nhất). */}
      <ul className={styles.chartLegend}>
        {bars.map((d, i) => (
          <li key={`${d.month}-${i}`} className={styles.chartLegendItem}>
            <span
              className={`${styles.chartSwatch} ${d.estimated ? styles.chartSwatchEstimated : ''}`}
              style={{ background: d.value === null ? BAR_COLORS.empty : d.color }}
              aria-hidden
            />
            <span>
              <strong>{d.label}</strong>: {d.value === null ? '—' : formatKc(d.value)}
              {d.estimated && d.value !== null && (
                <>
                  {' '}
                  <span className={styles.estimateTag}>{ESTIMATE_TAG}</span>
                </>
              )}
              {d.subLabel && <span className={styles.muted}> · {d.subLabel}</span>}
            </span>
          </li>
        ))}
      </ul>
    </figure>
  );
};

export default MonthBars;
